import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseModalComponent } from 'src/app/core/base-modal.componant';
import { SessionService } from 'src/app/shared/services/session.service';
import { LeagueIterationService } from 'src/app/simulator-booking/services/league-iteration.service';
import { LeagueIteration } from 'src/app/simulator-booking/models/league-iteration';
import { provideNativeDateAdapter } from '@angular/material/core';
import moment from 'moment-timezone';

@Component({
    selector: 'clone-league-iteration-modal',
    templateUrl: './clone-league-iteration-modal.component.html',
    styleUrl: './clone-league-iteration-modal.component.scss',
    providers: [provideNativeDateAdapter()],
    standalone: false
})
export class CloneLeagueIterationModalComponent extends BaseModalComponent implements OnInit {

    public sourceIteration: LeagueIteration;
    public userTimezone: string;
    public form = new FormGroup({
        year: new FormControl(new Date().getFullYear(), {
            validators: Validators.required
        }),
        startDateOnly: new FormControl('', {
            validators: Validators.required
        }),
        endDateOnly: new FormControl('', {
            validators: Validators.required
        }),
        startTimeOnly: new FormControl('', {
            validators: Validators.required
        }),
        endTimeOnly: new FormControl('', {
            validators: Validators.required
        }),
        entryFee: new FormControl(0, {
            validators: [Validators.required, Validators.min(0)]
        }),
        notes: new FormControl(''),
        stripePaymentLink: new FormControl(''),
        copyMembers: new FormControl(false)
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: LeagueIteration,
        private sessionService: SessionService,
        private leagueIterationService: LeagueIterationService,
        public dialogRef: MatDialogRef<CloneLeagueIterationModalComponent>
    ) {
        super();
        this.sourceIteration = data;
        this.userTimezone = this.sessionService.getUser().timeZone;
    }

    ngOnInit(): void {
        // Pre-fill form with source iteration data
        this.form.patchValue({
            entryFee: this.sourceIteration.entryFee,
            notes: this.sourceIteration.notes,
            stripePaymentLink: this.sourceIteration.stripePriceId
        });

        // Set default dates for next year
        const nextYearStart = moment.tz(this.sourceIteration.startDate, this.sessionService.getUser().timeZone).add(1, 'year').startOf('day').toDate();
        const nextYearEnd = moment.tz(this.sourceIteration.endDate, this.sessionService.getUser().timeZone).add(1, 'year').startOf('day').toDate();
        this.form.get('startDateOnly').setValue(nextYearStart.toISOString());
        this.form.get('endDateOnly').setValue(nextYearEnd.toISOString());
        this.form.get('startTimeOnly').setValue(this.formatInUserTimezone(this.sourceIteration.startDate, 'HH:mm'));
        this.form.get('endTimeOnly').setValue(this.formatInUserTimezone(this.sourceIteration.endDate, 'HH:mm'));
        this.form.get('year').setValue(new Date(nextYearStart).getFullYear());
    }

    formatInUserTimezone(date: Date, format: string): string {
        return moment.utc(date).tz(this.userTimezone).format(format);
    }

    submit({ value, valid }): void {
        if (valid) {
            this.isLoading = true;
            const startDate = new Date(value.startDateOnly);
            const endDate = new Date(value.endDateOnly);
            
            const cloneData = {
                sourceIterationId: this.sourceIteration.id,
                ...value,
                startDateOnly: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
                endDateOnly: `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`,
                startTimeOnly: value.startTimeOnly ? `${value.startTimeOnly}:00` : '00:00:00',
                endTimeOnly: value.endTimeOnly ? `${value.endTimeOnly}:00` : '00:00:00',
                organizationId: this.sessionService.getUser().organizationId,
                createdBy: this.sessionService.getUserId(),
                timeZone: this.sessionService.getUser().timeZone,
                copyMembers: value.copyMembers,
                leagueId: this.sourceIteration.leagueId
            };

            this.leagueIterationService.create(cloneData).subscribe({
                next: (result) => {
                    if (result !== null && result !== undefined) {
                        this.dialogRef.close("save");
                    }
                },
                error: (error) => {
                    console.error('Error cloning iteration:', error);
                    this.setError(error, this.form);
                    this.isLoading = false;
                },
                complete: () => this.isLoading = false
            });
        }
    }
}

